import { useTheme } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import { Button, Menu, menuClasses } from "@kaltura/ds-react-components";
import { Calendar24Icon } from "@kaltura/ds-react-icons";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { FunctionComponent, MouseEvent, useState } from "react";
import { Breakpoint } from "@mui/material";
import { iframeMenuProps } from "./helper";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { AddToCalendarMenuItems } from "@kaltura/mediaspace-shared-entry";

const StyledButton = styled(Button)({
    minWidth: "fit-content",
});

const StyledAddToCalendarButton = ({
    className,
    handleClick,
    addToCalendarActionText,
    size,
    collapseToIconBreakpoint,
    translucent,
    open,
}: {
    className?: string;
    handleClick: (event: MouseEvent<HTMLButtonElement>) => void;
    addToCalendarActionText: string;
    size?: "large" | "medium";
    collapseToIconBreakpoint: Breakpoint;
    translucent?: boolean;
    open?: boolean;
}) => {
    const theme = useTheme();
    const hideLabel = useMediaQuery(theme.breakpoints.down(collapseToIconBreakpoint));

    return (
        <StyledButton
            id={"addToCalendarToggleBtn"}
            className={className}
            variant={"borderless"}
            color={translucent ? "translucent" : "secondary"}
            aria-haspopup={true}
            aria-expanded={open}
            onClick={handleClick}
            startIcon={<Calendar24Icon />}
            size={size}
            {...(open && { "aria-controls": "mediaCalendarToggleMenu" })}
        >
            {!hideLabel && addToCalendarActionText}
        </StyledButton>
    );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
    [`& .${menuClasses.paper}`]: {
        borderColor: "#333",
        backgroundColor: "#333",
    },
    [`& .${menuClasses.paper} li a`]: {
        color: theme.palette.common.white,
    },
    [`& .${menuClasses.paper} a:hover > div`]: {
        backgroundColor: "#444",
    },
    [`& .${menuClasses.paper} a:focus > div`]: {
        backgroundColor: "#444",
    },
}));

export interface AddToCalendarTextProps {
    addToCalendarActionText: string;
    addToCalendarAddedText: string;
}

export interface AddToCalendarProps {
    className?: string;
    sessionId: string;
    textProps: AddToCalendarTextProps;
    onMenuOpen?: () => void;
    size?: "large" | "medium";
    collapseToIconBreakpoint?: Breakpoint;
    translucent?: boolean;
}

/**
 * add-to-calendar component
 * renders a dropdown + menu containing calendar options
 */
const AddToCalendar: FunctionComponent<AddToCalendarProps> = ({
    className,
    sessionId,
    textProps: { addToCalendarActionText, addToCalendarAddedText },
    onMenuOpen,
    size,
    collapseToIconBreakpoint = "xs",
    translucent,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        onMenuOpen?.();
        setAnchorEl(event.currentTarget);
        sendAnalytics("Add to calendar - Media Hero - Menu open", ButtonClickAnalyticsType.MENU);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const sendAnalytics = (action: string, buttonType: number) => {
        sendButtonAnalytics(`Add to calendar - Media Hero - ${action}`, buttonType);
    };

    return (
        <>
            <StyledAddToCalendarButton
                handleClick={handleButtonClick}
                addToCalendarActionText={addToCalendarActionText}
                className={className}
                size={size}
                collapseToIconBreakpoint={collapseToIconBreakpoint}
                translucent={translucent}
                open={open}
            />
            <StyledMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                {...iframeMenuProps}
                id={"mediaCalendarToggleMenu"}
                slotProps={{
                    list: {
                        "aria-labelledby": "addToCalendarToggleBtn",
                    },
                }}
            >
                <AddToCalendarMenuItems
                    color={"white"}
                    entryId={sessionId}
                    analyticsValue={{ location: "Media Hero" }}
                    onItemClicked={handleMenuClose}
                />
            </StyledMenu>
        </>
    );
};

export default AddToCalendar;
